import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button, CircularProgress, Typography, FormControlLabel, Checkbox } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const DivisionPage = () => {
  useAuthCheck();
  const { divisionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [division, setDivision] = useState({});

  const initialValues = {
    divisionCode: division.divisionCode || '',
    description: division.description || '',
    sortOrder: division.sortOrder || 0,
    isActive: division.isActive !== undefined ? division.isActive : true,
  };

  useEffect(() => {
    const fetchDivision = async () => {
      try {
        setLoading(true);
        if (divisionId) {
          const res = await api.get(`/divisions/${divisionId}`);
          setDivision(res.data);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDivision();
  }, [divisionId]);

  const checkoutSchema = yup.object().shape({
    divisionCode: yup.string().required('Required'),
  });

  const handleFormSubmit = async (values) => {
    try {
      const method = divisionId ? 'PUT' : 'POST';
      const url = divisionId ? `/divisions/update/${divisionId}` : '/divisions/new';
      await api({ method, url, data: values });
      toast.success(divisionId ? 'Division updated successfully' : 'Division created successfully');
      navigate("/divisions");
    } catch (error) {
      setError(error);
    }
  };

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Division"
          subtitle={loading ? 'Loading...' : divisionId ? 'Update' : 'Create'}
        />
        {!loading && (
          <StyledButton onClick={() => navigate("/divisions")}>
            <ArrowBackIosNewIcon />
          </StyledButton>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">Error: {error?.message || ''}</Typography></FlexCenter>
      ) : (
        <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
          <Formik 
            initialValues={initialValues} 
            enableReinitialize 
            validationSchema={checkoutSchema} 
            onSubmit={handleFormSubmit}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Box 
                  display="grid" 
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                  gap={2} 
                  sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                >
                  <TextField
                    fullWidth 
                    required 
                    variant="filled" 
                    label="Division Code"
                    name="divisionCode" 
                    value={values.divisionCode}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    error={!!touched.divisionCode && !!errors.divisionCode}
                    helperText={touched.divisionCode && errors.divisionCode}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <TextField
                    fullWidth 
                    variant="filled" 
                    multiline 
                    rows={3} 
                    label="Description"
                    name="description" 
                    value={values.description}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <TextField
                    fullWidth 
                    variant="filled" 
                    type="number" 
                    label="Sort Order"
                    name="sortOrder" 
                    value={values.sortOrder}
                    onBlur={handleBlur} 
                    onChange={handleChange}
                    slotProps={{ input: { sx: { "& input": { textAlign: "right" } } } }}
                    sx={{ gridColumn: 'span 12' }}
                  />
                  <FormControlLabel
                    sx={{ gridColumn: 'span 12' }}
                    control={
                      <Checkbox 
                        name="isActive" 
                        checked={values.isActive} 
                        onChange={handleChange} 
                        color="primary" 
                      />
                    }
                    label="Is Active"
                  />
                  <Button 
                    fullWidth 
                    type="submit" 
                    variant="contained" 
                    color="secondary" 
                    sx={{ gridColumn: 'span 12', height: 42 }}
                  >
                    {divisionId ? "Update" : "Create"}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}
    </Box>
  );
};

export default DivisionPage;
